import {
	createSlice,
	type PayloadAction,
} from "@reduxjs/toolkit";

import type {
	TConversation,
	TConversationTurnPagePayload,
	TMessage,
	TTurnInfo,
	TAgent,
	TConversationHistoryState,
	TTurnFeedback,
} from "../../types/agent";

import { buildPaulTurnLoadErrorMessage } from "../../utils/paulTurnHistory";

import {
	getConversationMessagesById,
	getConversations,
	updateConversationTitle,
	submitFeedback,
	getAgentMeta,
	getTurnInfo,
	getConversationTurnPage,
	deleteConversation,
} from "./agent.thunk";

type TAgentState = {
	conversations: Array<TConversation>;
	selectedConversation: TConversation | null;
	messages: Array<TMessage>;
	loadingConversations: boolean;
	loadingMessages: boolean;
	conversationsError: string | null;
	messagesError: string | null;
	updatingTitle: boolean;
	updateTitleError: string | null;
	pollingMessageId: string | null;
	agents: Record<string, TAgent>;
	defaultAgentId: string | null;
	defaultDatasetId: string | null;
	loadingAgents: boolean;
	agentsError: string | null;
	loadedConversationId: string | null;
	messageHistory: TConversationHistoryState;
	turnFeedbackByTurn: Record<number, TTurnFeedback | null>;
};

const createHistoryRequestState = () => ({
	status: "idle" as const,
	error: null,
	lastLoadedCount: 0,
});

const createInitialHistoryState =
	(): TConversationHistoryState => ({
		totalTurns: 0,
		loadedTurnIndices: [],
		hasOlderTurns: false,
		initialRequest: createHistoryRequestState(),
		loadMoreRequest: createHistoryRequestState(),
	});

const sortMessagesByTime = (messages: Array<TMessage>) => {
	messages.sort((a, b) => {
		const aHasTurn = a.turn_idx >= 0;
		const bHasTurn = b.turn_idx >= 0;

		if (aHasTurn && bHasTurn) {
			if (a.turn_idx !== b.turn_idx) {
				return a.turn_idx - b.turn_idx;
			}

			const roleOrder =
				(a.role === "user" ? 0 : 1) -
				(b.role === "user" ? 0 : 1);
			if (roleOrder !== 0) {
				return roleOrder;
			}

			if (
				a.turn_load_error === true &&
				b.turn_load_error !== true
			) {
				return 1;
			}

			if (
				a.turn_load_error !== true &&
				b.turn_load_error === true
			) {
				return -1;
			}
		}

		return a.time_millis - b.time_millis;
	});
};

const syncLoadedTurnIndices = (
	state: TAgentState,
	turnIndices: Array<number>
) => {
	if (turnIndices.length === 0) {
		state.messageHistory.hasOlderTurns =
			state.messageHistory.totalTurns > 0 &&
			state.messageHistory.loadedTurnIndices.length === 0
				? true
				: state.messageHistory.loadedTurnIndices.length >
						0 &&
					Math.min(
						...state.messageHistory.loadedTurnIndices
					) > 0;
		return;
	}

	const mergedTurnIndices = new Set(
		state.messageHistory.loadedTurnIndices
	);
	turnIndices
		.filter((turnIdx) => turnIdx >= 0)
		.forEach((turnIdx) => {
			mergedTurnIndices.add(turnIdx);
			state.messageHistory.totalTurns = Math.max(
				state.messageHistory.totalTurns,
				turnIdx + 1
			);
		});

	state.messageHistory.loadedTurnIndices = Array.from(
		mergedTurnIndices
	).sort((a, b) => a - b);
	state.messageHistory.hasOlderTurns =
		state.messageHistory.loadedTurnIndices.length > 0 &&
		Math.min(...state.messageHistory.loadedTurnIndices) > 0;
};

const upsertMessages = (
	state: TAgentState,
	messages: Array<TMessage>
) => {
	const resolvedTurnIndices = new Set(
		messages
			.filter(
				(message) =>
					message.turn_idx >= 0 &&
					message.turn_load_error !== true
			)
			.map((message) => message.turn_idx)
	);

	if (resolvedTurnIndices.size > 0) {
		state.messages = state.messages.filter(
			(message) =>
				!(
					message.turn_load_error === true &&
					resolvedTurnIndices.has(message.turn_idx)
				)
		);
	}

	messages.forEach((message) => {
		const existingIndex = state.messages.findIndex(
			(existingMessage) =>
				existingMessage.msg_id === message.msg_id &&
				existingMessage.role === message.role
		);

		if (existingIndex !== -1) {
			state.messages[existingIndex] = message;
		} else {
			state.messages.push(message);
		}
	});

	sortMessagesByTime(state.messages);
	syncLoadedTurnIndices(
		state,
		messages.map((message) => message.turn_idx)
	);
};

const upsertTurnFeedback = (
	state: TAgentState,
	turns: Array<TTurnInfo>
) => {
	turns.forEach((turn) => {
		state.turnFeedbackByTurn[turn.turn_idx] = turn.feedback;
	});
};

const initialState: TAgentState = {
	conversations: [],
	selectedConversation: null,
	messages: [],
	loadingConversations: false,
	loadingMessages: false,
	conversationsError: null,
	messagesError: null,
	updatingTitle: false,
	updateTitleError: null,
	pollingMessageId: null,
	agents: {},
	defaultAgentId: null,
	defaultDatasetId: null,
	loadingAgents: false,
	agentsError: null,
	loadedConversationId: null,
	messageHistory: createInitialHistoryState(),
	turnFeedbackByTurn: {},
};

export const agentSlice = createSlice({
	name: "agent",
	initialState,
	reducers: {
		setSelectedConversation(
			state,
			action: PayloadAction<TConversation | null>
		) {
			state.selectedConversation = action.payload;
		},
		setPollingMessageId(
			state,
			action: PayloadAction<string | null>
		) {
			state.pollingMessageId = action.payload;
		},
		updateConversationTitleInState(
			state,
			action: PayloadAction<{
				conv_id: string;
				title: string;
			}>
		) {
			const { conv_id, title } = action.payload;
			const conversation = state.conversations.find(
				(c) => c.conv_id === conv_id
			);
			if (conversation) {
				conversation.title = title;
			}
			if (
				state.selectedConversation &&
				state.selectedConversation.conv_id === conv_id
			) {
				state.selectedConversation.title = title;
			}
		},
		addUserMessage(state, action: PayloadAction<TMessage>) {
			upsertMessages(state, [action.payload]);
		},
		addModelMessage(
			state,
			action: PayloadAction<TMessage>
		) {
			upsertMessages(state, [action.payload]);
		},
		removeMessageById(
			state,
			action: PayloadAction<string>
		) {
			state.messages = state.messages.filter(
				(message) => message.msg_id !== action.payload
			);
		},
		syncConversationTurnCount(
			state,
			action: PayloadAction<{
				conv_id: string;
				num_turns: number;
			}>
		) {
			const { conv_id, num_turns } = action.payload;
			const conversation = state.conversations.find(
				(conv) => conv.conv_id === conv_id
			);

			if (conversation) {
				conversation.num_turns = Math.max(
					conversation.num_turns ?? 0,
					num_turns
				);
			}

			if (
				state.selectedConversation &&
				state.selectedConversation.conv_id === conv_id
			) {
				state.selectedConversation.num_turns = Math.max(
					state.selectedConversation.num_turns ?? 0,
					num_turns
				);
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getConversations.pending, (state) => {
				state.loadingConversations = true;
				state.conversationsError = null;
			})
			.addCase(
				getConversations.fulfilled,
				(
					state,
					action: PayloadAction<Array<TConversation>>
				) => {
					state.loadingConversations = false;
					state.conversations = action.payload;
				}
			)
			.addCase(
				getConversations.rejected,
				(state, action) => {
					state.loadingConversations = false;
					state.conversationsError =
						action.payload as string;
				}
			)
			.addCase(
				getConversationMessagesById.pending,
				(state, action) => {
					state.messagesError = null;
					// Only set loading if switching to a different conversation
					const newConvId = action.meta.arg;
					if (state.loadedConversationId !== newConvId) {
						// Clear messages when loading a different conversation
						state.messages = [];
						state.loadedConversationId = newConvId;
						state.loadingMessages = true;
						state.messageHistory =
							createInitialHistoryState();
						state.turnFeedbackByTurn = {};
					}
				}
			)
			.addCase(
				getConversationMessagesById.fulfilled,
				(state, action) => {
					if (
						state.loadedConversationId !== action.meta.arg
					) {
						return;
					}
					state.loadingMessages = false;
					// Don't clear messages here - already cleared in pending if needed
					// Messages will be populated by getTurnInfo
					if (
						state.selectedConversation?.conv_id ===
						action.payload.conv_id
					) {
						state.selectedConversation = {
							...state.selectedConversation,
							...action.payload,
						};
					} else {
						state.selectedConversation = action.payload;
					}

					const totalTurns = action.payload.num_turns ?? 0;
					state.messageHistory.totalTurns = totalTurns;
					state.messageHistory.hasOlderTurns =
						state.messageHistory.loadedTurnIndices.length >
						0
							? Math.min(
									...state.messageHistory.loadedTurnIndices
								) > 0
							: totalTurns > 0;

					if (totalTurns === 0) {
						state.messageHistory.initialRequest = {
							status: "empty",
							error: null,
							lastLoadedCount: 0,
						};
						state.messageHistory.loadMoreRequest = {
							status: "empty",
							error: null,
							lastLoadedCount: 0,
						};
					} else if (
						state.messageHistory.loadedTurnIndices
							.length === 0 &&
						state.messageHistory.initialRequest.status ===
							"idle"
					) {
						state.messageHistory.initialRequest.status =
							"loading";
					}
				}
			)
			.addCase(
				getConversationMessagesById.rejected,
				(state, action) => {
					if (
						state.loadedConversationId !== action.meta.arg
					) {
						return;
					}
					state.loadingMessages = false;
					state.messagesError = action.payload as string;
					state.loadedConversationId = null;
					state.messageHistory =
						createInitialHistoryState();
					state.turnFeedbackByTurn = {};
				}
			)
			.addCase(updateConversationTitle.pending, (state) => {
				state.updatingTitle = true;
				state.updateTitleError = null;
			})
			.addCase(
				updateConversationTitle.fulfilled,
				(state, action: PayloadAction<TConversation>) => {
					state.updatingTitle = false;
					const updatedConversation = action.payload;
					const index = state.conversations.findIndex(
						(conv) =>
							conv.conv_id === updatedConversation.conv_id
					);
					if (index !== -1) {
						state.conversations[index] =
							updatedConversation;
					}
					if (
						state.selectedConversation &&
						state.selectedConversation.conv_id ===
							updatedConversation.conv_id
					) {
						state.selectedConversation =
							updatedConversation;
					}
				}
			)
			.addCase(
				updateConversationTitle.rejected,
				(state, action) => {
					state.updatingTitle = false;
					state.updateTitleError = action.payload as string;
				}
			)
			.addCase(
				deleteConversation.fulfilled,
				(state, action: PayloadAction<string>) => {
					const deletedConversationId = action.payload;
					state.conversations = state.conversations.filter(
						(conversation) =>
							conversation.conv_id !== deletedConversationId
					);

					if (
						state.selectedConversation?.conv_id ===
						deletedConversationId
					) {
						state.selectedConversation = null;
						state.messages = [];
						state.messagesError = null;
						state.loadedConversationId = null;
						state.pollingMessageId = null;
						state.messageHistory =
							createInitialHistoryState();
						state.turnFeedbackByTurn = {};
					}
				}
			)
			.addCase(
				submitFeedback.fulfilled,
				(
					state,
					action: PayloadAction<{
						turnIdx: number;
						reason: string;
					}>
				) => {
					const { turnIdx, reason } = action.payload;
					state.turnFeedbackByTurn[turnIdx] = {
						is_upvote: null,
						comments: reason,
					};
				}
			)
			.addCase(getAgentMeta.pending, (state) => {
				state.loadingAgents = true;
				state.agentsError = null;
			})
			.addCase(
				getAgentMeta.fulfilled,
				(
					state,
					action: PayloadAction<{
						agents: Record<string, TAgent>;
						default_agent_id: string;
						assisted_dataset_id: string;
					}>
				) => {
					state.loadingAgents = false;
					state.agents = action.payload.agents;
					state.defaultAgentId =
						action.payload.default_agent_id;
					state.defaultDatasetId =
						action.payload.assisted_dataset_id;
				}
			)
			.addCase(getAgentMeta.rejected, (state, action) => {
				state.loadingAgents = false;
				state.agentsError = action.payload as string;
			})
			.addCase(
				getConversationTurnPage.pending,
				(state, action) => {
					const { conversationId, requestKind } =
						action.meta.arg;
					if (
						state.loadedConversationId !== conversationId
					) {
						return;
					}

					if (requestKind === "initial") {
						state.messageHistory.initialRequest.status =
							"loading";
						state.messageHistory.initialRequest.error =
							null;
						state.messageHistory.initialRequest.lastLoadedCount = 0;
						state.messagesError = null;
						return;
					}

					state.messageHistory.loadMoreRequest.status =
						"loading";
					state.messageHistory.loadMoreRequest.error = null;
					state.messageHistory.loadMoreRequest.lastLoadedCount = 0;
				}
			)
			.addCase(
				getConversationTurnPage.fulfilled,
				(
					state,
					action: PayloadAction<TConversationTurnPagePayload>
				) => {
					const {
						conversationId,
						requestKind,
						turns,
						turnIndices,
						failures,
					} = action.payload;
					if (
						state.loadedConversationId !== conversationId
					) {
						return;
					}
					const turnMessages = turns.flatMap(
						(turn) => turn.messages
					);
					const failedTurnMessages = failures.map(
						(failure) =>
							buildPaulTurnLoadErrorMessage(
								conversationId,
								failure.turn_idx,
								failure.error
							)
					);

					upsertMessages(state, turnMessages);
					upsertTurnFeedback(state, turns);
					upsertMessages(state, failedTurnMessages);
					syncLoadedTurnIndices(state, [
						...turns.map((turn) => turn.turn_idx),
						...failures.map((failure) => failure.turn_idx),
					]);

					const requestState =
						requestKind === "initial"
							? state.messageHistory.initialRequest
							: state.messageHistory.loadMoreRequest;

					requestState.status =
						turnIndices.length === 0 ? "empty" : "success";
					requestState.error = null;
					requestState.lastLoadedCount =
						turns.length + failures.length;

					if (
						requestKind === "initial" &&
						turnMessages.length === 0 &&
						failedTurnMessages.length === 0
					) {
						requestState.status = "empty";
					}
				}
			)
			.addCase(
				getConversationTurnPage.rejected,
				(state, action) => {
					const { conversationId, requestKind } =
						action.meta.arg;
					if (
						state.loadedConversationId !== conversationId
					) {
						return;
					}
					const targetRequestState =
						requestKind === "initial"
							? state.messageHistory.initialRequest
							: state.messageHistory.loadMoreRequest;

					targetRequestState.status = "error";
					targetRequestState.error =
						typeof action.payload === "string"
							? action.payload
							: "Paul could not load this part of the conversation.";
					targetRequestState.lastLoadedCount = 0;

					if (requestKind === "initial") {
						state.messagesError = targetRequestState.error;
					}
				}
			)
			.addCase(
				getTurnInfo.fulfilled,
				(state, action: PayloadAction<TTurnInfo>) => {
					if (
						state.loadedConversationId !==
						action.payload.conv_id
					) {
						return;
					}
					state.turnFeedbackByTurn[
						action.payload.turn_idx
					] = action.payload.feedback;
					upsertMessages(state, action.payload.messages);
				}
			);
	},
});

export const {
	setSelectedConversation,
	addUserMessage,
	setPollingMessageId,
	updateConversationTitleInState,
	addModelMessage,
	removeMessageById,
	syncConversationTurnCount,
} = agentSlice.actions;
export const agentReducer = agentSlice.reducer;
